import React from "react";
import { Target, ChevronRight, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { truncateToWords } from "../lib/campaignStatsUtils";

interface ObjectiveBarProps {
  description?: string;
  viewBriefOpen: boolean;
  onViewBriefOpenChange: (open: boolean) => void;
}

export const ObjectiveBar: React.FC<ObjectiveBarProps> = ({
  description,
  viewBriefOpen,
  onViewBriefOpenChange,
}) => {
  return (
    <div className="bg-[#FAF9FF] border border-[#E9D5FF]/60 rounded-2xl p-5 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
      <div className="flex items-start gap-4 flex-1">
        <div className="w-12 h-12 flex items-center justify-center bg-[#E9D5FF80] border border-indigo-100 rounded-xl text-[#A655F8] flex-shrink-0">
          <Target className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[#A655F8] font-bold text-[10px] tracking-wider uppercase">
            Campaign Objective
          </div>
          <p className="text-slate-700 text-sm font-medium mt-1">
            {truncateToWords(
              description ||
                "Increase brand awareness and drive engagement through content creator campaigns.",
              12
            )}
          </p>
        </div>
      </div>

      <Dialog open={viewBriefOpen} onOpenChange={onViewBriefOpenChange}>
        <DialogTrigger asChild>
          <Button
            variant="outline"
            className="bg-white hover:bg-purple-50/50 text-[#A655F8] hover:text-[#9333EA] border-[#E9D5FF] hover:border-[#D8B4FE] font-semibold rounded-xl px-5 py-2.5 flex items-center gap-2 shadow-sm transition-all shrink-0"
          >
            View Brief
            <ChevronRight className="w-4 h-4 text-[#A655F8]" />
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-2xl bg-white rounded-[28px] p-10 md:p-12 border-0 shadow-2xl [&>button]:!border [&>button]:!border-slate-200 [&>button]:!rounded-full [&>button]:!p-1.5 [&>button]:!text-slate-400 [&>button]:hover:!text-slate-600 [&>button]:!right-6 [&>button]:!top-6 [&>button]:!transition-all [&>button]:!bg-white [&>button]:!shadow-sm">
          <DialogHeader className="space-y-0 text-left">
            <div className="flex items-center gap-4">
              {/* Glowing Icon Container */}
              <div className="relative flex-shrink-0 select-none">
                {/* Sparkle top right */}
                <span className="absolute -top-1.5 -right-1 text-[#A655F8]/80 animate-pulse">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 3l1.912 5.088L19 10l-5.088 1.912L12 17l-1.912-5.088L5 10l5.088-1.912z" />
                  </svg>
                </span>

                {/* Sparkle bottom left */}
                <span className="absolute -bottom-1 -left-1.5 text-[#A655F8]/80 animate-pulse">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 3l1.912 5.088L19 10l-5.088 1.912L12 17l-1.912-5.088L5 10l5.088-1.912z" />
                  </svg>
                </span>

                {/* Main outer ring with soft radial glow */}
                <div className="w-16 h-16 rounded-full bg-gradient-to-tr from-purple-100 to-indigo-50 flex items-center justify-center shadow-[0_0_20px_rgba(166,85,248,0.15)] border border-purple-200/50">
                  <div className="w-11 h-11 rounded-full bg-[#E9D5FF]/60 flex items-center justify-center">
                    <Target className="w-6 h-6 text-[#A655F8]" />
                  </div>
                </div>
              </div>

              {/* Title & Accent Underline */}
              <div className="flex flex-col">
                <DialogTitle className="text-3xl font-bold tracking-tight text-[#0F172A] font-serif">
                  Campaign Objective
                </DialogTitle>
                <div className="w-14 h-[3px] bg-[#A655F8] rounded-full mt-2" />
              </div>
            </div>
          </DialogHeader>

          <div className="mt-8 space-y-3">
            <div className="flex items-center gap-2 text-[#A655F8] font-bold text-[11px] tracking-widest uppercase">
              <FileText className="w-4 h-4" />
              <span>Description</span>
            </div>
            <p className="text-slate-600 text-[15px] leading-relaxed whitespace-pre-line">
              {description || "No description provided for this campaign."}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
